<template>
<div ref="swiperContainer" class="swiper-container h-full">
  <ul id="warehouseList" class="swiper-wrapper flex items-center" :style="warehouseListStyle">
    <li
      v-for="(w,i) in warehouses"
      :key="w.id"
      :ref="'w-' + w.id"
      class="swiper-slide inline-block mx-auto"
      :class="`warehouse-id-${w.id} code-${w.display_number}`"
      @click="selectItem(w, i)"
    >
      <warehouse-item-content :warehouse="w" :selected="w.id == selectedWarehouse.id" :index="i" :is-mobile="isMobile" />
    </li>
  </ul>
  <div v-if="!isMobile" class="swiper-scrollbar"/>
</div>
</template>

<style lang="scss" scoped>
@media (min-width: theme('screens.md')) {
  .swiper-wrapper {
    height: 100%;
  }
  ul > li {
    @apply border-gray-300 inline-block h-full;
    &.swiper-slide {
      height: auto !important;
    }
  }
  .swiper-scrollbar {
    background-color: white;
  }
  ::v-deep .swiper-scrollbar-drag {
    background-color: theme('colors.gray.500');
    height: 20px;
  }
}

::v-deep {
  .warehouse-item {
    @apply relative;
    padding: 10px;
    min-width: calc(100vw - 48px);
    color: theme('colors.gray.900');
    .photo {
      @apply w-20 h-20 border border-white;
    }
    .distance {
      font-size: 11px;
      color: theme('colors.gray.600');
      b {
        @apply text-sm;
        color: theme('colors.gray.900');
        font-weight: 500;
      }
    }
    .warehouse-info {
      font-size: 11px;
    }
    .badge {
      @apply rounded inline-block mr-2 whitespace-no-wrap flex items-center;
      font-weight: 500;
      padding: 2.5px 4.5px;
      line-height: 12px;
    }
    .temperature.normal {
      @apply text-white bg-gray-600;
    }
    .temperature.constant {
      @apply bg-gray-300;
    }

    .supported-cargo > .badge {
      @apply bg-gray-300;
    }
    .weekday-hours, .holidays {
      border: 1px solid theme('colors.gray.600');
    }
    &.selected {
      @apply bg-gray-100;
      .distance {
        border-width: 0;
      }
    }
    .contact-link {
      @apply inline-flex justify-center items-center rounded-full text-center whitespace-no-wrap bg-orange cursor-pointer absolute top-0 right-0;
    }
    .contact-icon {
      filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%);
      height: 1.25rem;
    }
    .contact-text {
      @apply text-xs text-white;

      span {
        @apply hidden;
      }
    }
    @media (min-width: theme('screens.md')) {
      @apply p-4;
      min-width: 360px;
      width: auto;

      .contact-link {
        @apply static;
      }

      .contact-text span {
        @apply inline;
      }
    }
  }

  // For IOS scroolbar issue
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
    height: 3px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background-color: rgba(0,0,59,.4);
    -webkit-box-shadow: 0 0 1px rgba(0, 0, 59,.4);
  }
}
</style>

<script>
import Swiper from 'swiper';
import warehouseItemContent from '@/components/warehouses/search/map/warehouseItemContent.vue';

export default {
  components: { warehouseItemContent },
  props: {
    warehouses: { type: Array, required: true },
    selectedWarehouse: { type: Object, default: null },
    isMobile: { type: Boolean, required: true },
    listHeight: { type: Number }
  },
  data () {
    return {
      swiperInstance: null,
      sliderOptions: this.getSliderOptions()
    };
  },
  mounted () {
    this.createSwiperInstance();
  },
  beforeDestroy() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy();
      this.swiperInstance = null;
    }
  },
  watch: {
    selectedWarehouse (next, prev) {
      if (next && (next.id !== (prev || prev.id))) {
        this.$nextTick(() => {
          if (this.swiperInstance) {
            this.swiperInstance.slideTo(next.index, 1000, false);
          }
        });
      }
    },
    isMobile (next, prev) {
      if (prev !== next) {
        // Force re-render by updating the slider options
        if (this.swiperInstance) {
          this.swiperInstance.destroy();
          this.swiperInstance = null;
        }
        this.$nextTick(() => {
          this.sliderOptions = this.getSliderOptions();
          this.createSwiperInstance();
        });
      }
    }
  },
  computed: {
    warehouseListStyle () {
      return !this.isMobile ? 'height:' + this.listHeight + 'px' : ''
    }
  },
  methods: {
    getSliderOptions () {
      return {
        initialSlide: 0,
        mousewheel: {
          enabled: true
        },
        freeMode: this.isMobile ? false : true,
        slidesPerView: this.isMobile ? 1.15 : 'auto',
        spaceBetween: this.isMobile ? 8 : 0,
        direction: this.isMobile ? 'horizontal' : 'vertical',
        centeredSlides: this.isMobile,
        cssMode: true,
        // Disable preloading of all images
        preloadImages: false,
        // Enable lazy loading
        lazy: {
          loadPrevNext: true,
          loadPrevNextAmount: 5,
          scrollingElement: '#warehouseList'
        },
        scrollbar: this.isMobile ? false : {
          el: '.swiper-scrollbar',
          draggable: true
        }
      };
    },
    createSwiperInstance() {
      this.sliderOptions = this.getSliderOptions();
      this.swiperInstance = new Swiper(this.$refs.swiperContainer, this.sliderOptions);
    },

    selectItem (w, i) {
      this.$emit('select', w, i);
    }
  }
};
</script>
